import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import Drawer from "@via/components/drawer.tsx";
import { GenericError } from "@via/components/error.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table, { DrawerSize } from "@via/components/table/table.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import useUrlParams from "@via/hooks/useURLParams.tsx";
import CreateAIModelForm from "@via/pages/cortex/model-garden/create-ai-model-form.tsx";
import EditAIModelForm from "@via/pages/cortex/model-garden/edit-ai-model-form.tsx";
import ModelGardenFilter from "@via/pages/cortex/model-garden/model-garden-filter.tsx";
import {
  useAIModelsList,
  useDeleteAIModel,
} from "@via/pages/cortex/model-garden/model-garden.api.tsx";
import {
  BUTTONS,
  EMPTY_TABLE_MESSAGE,
  ERROR,
  ROUTES,
} from "@via/utils/constants.tsx";
import { classNames, getElapsedTime } from "@via/utils/helpers.tsx";
import { AIModelListItem, AIModelType, CortexRegistryType } from "common";
import { useCallback, useEffect, useState, type JSX } from "react";
import GCSLogo from "/gcs-logo.svg";
import SageMakerLogo from "/sagemaker-logo.svg";
import TamarindLogo from "/tamarind-logo.svg";
import { useCortexContext } from "@via/context/cortex-context";

const defaultParams = {
  sort: [{ id: "createdAt", desc: true }],
  filters: [],
  page: 0,
  pageSize: 10,
};

// Platform badge component - maps registry enum to display
const PlatformBadge = ({ registry }: { registry: CortexRegistryType }) => {
  const registryConfig: Record<
    CortexRegistryType,
    { bgColor?: string; letter?: string; name: string; icon?: any }
  > = {
    [CortexRegistryType.VertexAI]: {
      name: "Vertex AI",
      icon: GCSLogo,
    },
    [CortexRegistryType.Tamarind]: {
      name: "Tamarind",
      icon: TamarindLogo,
    },
    [CortexRegistryType.SageMaker]: {
      name: "SageMaker",
      icon: SageMakerLogo,
    },
  };

  const config = registryConfig[registry];

  if (!config) return <span>Unknown</span>;

  return (
    <div className="flex items-center gap-2">
      {config.icon ? (
        <img
          className="mr-2 h-4 w-4"
          alt={`${config.name} Logo`}
          src={config.icon}
        />
      ) : (
        <div
          className={classNames(
            "flex h-6 w-6 items-center justify-center rounded text-xs font-semibold text-white bg-blue-500"
          )}>
          {config.name.charAt(0).toUpperCase()}
        </div>
      )}

      <span>{config.name}</span>
    </div>
  );
};
// Type badge component
const TypeBadge = ({ type }: { type: AIModelType }) => {
  const typeConfig: Record<
    AIModelType,
    { label: string; bgColor: string; textColor: string }
  > = {
    [AIModelType.Endpoint]: {
      label: "Endpoint",
      bgColor: "bg-blue-100",
      textColor: "text-blue-800",
    },
    [AIModelType.Model]: {
      label: "Model",
      bgColor: "bg-purple-100",
      textColor: "text-purple-800",
    },
  };

  const config = typeConfig[type];

  if (!config) {
    return <span>-</span>;
  }

  return (
    <span
      className={classNames(
        config.bgColor,
        config.textColor,
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap"
      )}>
      {config.label}
    </span>
  );
};

const MODEL_TABLE_OPTIONS = [
  { name: "Edit", href: "#" },
  { name: "Delete", href: "#" },
];

const ModelGarden = (): JSX.Element => {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [open, setDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const {
    isDrawerOpen: isCortexDrawerOpen,
    setShouldUsePortalForCortexDrawer,
  } = useCortexContext();

  // While Model Garden create/edit drawers are open (blocking overlays),
  // force Cortex into portal/overlay mode so it can be opened on top.
  useEffect(() => {
    const wantsPortal = open || isEditDrawerOpen;
    if (wantsPortal) {
      setShouldUsePortalForCortexDrawer(true);
      return;
    }
    if (!isCortexDrawerOpen) setShouldUsePortalForCortexDrawer(false);
  }, [
    open,
    isEditDrawerOpen,
    isCortexDrawerOpen,
    setShouldUsePortalForCortexDrawer,
  ]);

  useEffect(() => {
    return () => {
      setShouldUsePortalForCortexDrawer(false);
    };
  }, [setShouldUsePortalForCortexDrawer]);

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(initialParams.filters);
  const [modelToBeDeleted, setModelToBeDeleted] =
    useState<AIModelListItem | null>(null);
  const [modelToBeEdited, setModelToBeEdited] =
    useState<AIModelListItem | null>(null);

  const {
    data: modelsList,
    isError: isModelsListError,
    isLoading: isModelsListLoading,
  } = useAIModelsList(page, pageSize, sort, columnFilters);

  const { mutateAsync: deleteModel, isPending: isDeleteModelLoading } =
    useDeleteAIModel();

  const handleContextMenuAction = (
    name: string,
    model: AIModelListItem
  ): void => {
    switch (name) {
      case "Edit":
        setModelToBeEdited(model);
        setIsEditDrawerOpen(true);
        break;
      case "Delete":
        setModelToBeDeleted(model);
        break;
    }
  };

  useEffect(() => {
    setUrlParams({ sort, filters: columnFilters, page, pageSize });
  }, [sort, columnFilters, page, pageSize]);

  const getModelGardenContext = useCallback(() => {
    const models = modelsList?.data || [];
    const total = modelsList?.total || 0;

    return {
      models: models.map(model => ({
        id: model.id,
        name: model.name,
        owner: model.owner,
        createdAt: model.createdAt,
        updatedAt: model.updatedAt,
      })),
      total: total,
      currentPage: page,
      pageSize: pageSize,
      filters: columnFilters,
      sortBy: sort.length > 0 ? sort[0] : null,
    };
  }, [modelsList, page, pageSize, sort, columnFilters]);

  useRegisterCortexContext({
    routePattern: ROUTES.CORTEX_MODEL_GARDEN,
    contextName: "Model Garden",
    getContext: getModelGardenContext,
    systemPrompt:
      "You are analyzing model garden page. Help answer questions about model garden, help find specific models, and assist with filtering and sorting.",
  });

  const columnHelper = createColumnHelper<AIModelListItem>();
  const columns = [
    columnHelper.accessor("name", {
      cell: ({ row }) => {
        return (
          <button
            className={classNames("via-link", "truncate")}
            onClick={e => {
              e.preventDefault();
              handleContextMenuAction("Edit", row.original);
            }}>
            {row.original.name}
          </button>
        );
      },
      header: () => <span>Name</span>,
      size: 150,
    }),
    columnHelper.accessor("publisher", {
      id: "provider",
      cell: info => {
        const publisher = info.getValue();
        return <span>{publisher}</span>;
      },
      header: () => <span>Publisher</span>,
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor("modelName", {
      id: "modelName",
      cell: info => {
        const modelName = info.getValue();
        return <span className="block truncate">{modelName}</span>;
      },
      header: () => <span>Model</span>,
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor("version", {
      cell: info => {
        const version = info.getValue();
        return <span className="block truncate">{version || "-"}</span>;
      },
      header: () => <span>Version</span>,
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor("registry", {
      cell: info => {
        return (
          <span className="block truncate">
            <PlatformBadge registry={info.getValue()} />
          </span>
        );
      },
      header: () => <span>Platform</span>,
      size: 150,
      enableSorting: false,
    }),
    columnHelper.accessor("type", {
      cell: info => {
        return <TypeBadge type={info.getValue()} />;
      },
      header: () => <span>Type</span>,
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor("owner", {
      cell: info => {
        return <span>{info.getValue()}</span>;
      },
      header: () => "Owner",
      size: 120,
      enableSorting: false,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{getElapsedTime(value.toString())}</span>;
      },
      header: () => <span>Date Registered</span>,
      size: 130,
    }),
    columnHelper.accessor("updatedAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{getElapsedTime(value.toString())}</span>;
      },
      header: () => <span>Last Modified</span>,
      size: 130,
    }),
    columnHelper.accessor("id", {
      cell: ({ row }) => {
        const contextMenuOptions = MODEL_TABLE_OPTIONS;

        return (
          <TableContextMenu
            options={contextMenuOptions}
            onSelect={option => {
              handleContextMenuAction(option.name, row.original);
            }}
          />
        );
      },
      header: () => "Options",
      size: 70,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<AIModelListItem>>;

  return (
    <main className="py-4">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">AI Models</h2>
        <ActionButton
          button={BUTTONS.REGISTER_MODEL}
          primary={true}
          onClick={() => {
            setDrawerOpen(true);
          }}
        />
      </div>
      <Drawer
        id="cortex-create-ai-model"
        open={open}
        setOpen={setDrawerOpen}
        size={DrawerSize.Medium}>
        <CreateAIModelForm setOpen={setDrawerOpen} />
      </Drawer>

      {modelToBeEdited ? (
        <Drawer
          id="cortex-edit-ai-model"
          open={isEditDrawerOpen}
          setOpen={setIsEditDrawerOpen}
          size={DrawerSize.Big}>
          <EditAIModelForm
            aiModel={modelToBeEdited}
            setOpen={setIsEditDrawerOpen}
          />
        </Drawer>
      ) : null}

      <DeleteModal
        item={modelToBeDeleted}
        setItem={setModelToBeDeleted}
        deleteFunction={() => deleteModel(modelToBeDeleted!.id)}
        isLoading={isDeleteModelLoading}
        title="Delete AI Model"
      />

      {isModelsListError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isModelsListLoading}
          data={modelsList?.data ?? []}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={modelsList?.total}
          emptyMessage={EMPTY_TABLE_MESSAGE.DEFAULT}
          applyColumnSize={true}
          additionalFilters={
            <ModelGardenFilter
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setPage={setPage}
              setPageSize={setPageSize}
            />
          }
        />
      )}
    </main>
  );
};

export default ModelGarden;
